// components/LeftPanel.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotesTreeList from './LeftPanel/NotesTreeList';
import styles from './LeftPanel.module.css';
import AddCategory from './LeftPanel/AddCategory';
import { logoutUser, getCurrentUser } from './utils/authService';
import { useNotes } from './contexts/NotesContext'; // Add this import
import { useCategories } from './contexts/CategoriesContext';
import { usePanelContext } from './contexts/PanelContext';
import { ChevronLeft, Menu, Settings, Search, Plus } from 'lucide-react';
import SearchModal from './SearchModal';
import CategoryFilterModal from './LeftPanel/CategoryFilterModal';
import AddCategoryModal from './LeftPanel/AddCategoryModal';


const LeftPanel = ({ onNoteSelect, selectedNoteId, notes }) => {
  const navigate = useNavigate();
  const { categories, loading, error, addCategory } = useCategories();
  const { refreshNotes } = useNotes(); // Add this line
  const { isLeftPanelVisible, toggleLeftPanel } = usePanelContext();
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [selectedViewId, setSelectedViewId] = useState('all');
  const [savedViews, setSavedViews] = useState([]);
  const [visibleCategoryIds, setVisibleCategoryIds] = useState([]);
  const [modalVisibleCategories, setModalVisibleCategories] = useState([]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);
  const handleSearchClick = () => setIsSearchModalOpen(true);
  const handleCloseSearchModal = () => setIsSearchModalOpen(false);

  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest', 'title'
  const [folderSort, setFolderSort] = useState('none'); 

  const [expandedCategories, setExpandedCategories] = useState(() => {
    const saved = localStorage.getItem('expandedCategories');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

const handleViewChange = (viewId) => {
  console.log('Selected View ID:', viewId);
  const parsedId = viewId === 'all' ? 'all' : parseInt(viewId, 10);
  setSelectedViewId(viewId);

  if (parsedId === 'all') {
    console.log('Setting all categories visible');
    setVisibleCategoryIds(categories.map(cat => cat.id));
    return;
  }

  const view = savedViews.find((v) => v.id === parsedId);
  console.log('Matched view object:', view);
  if (view) {
    try {
      const categoryIds = JSON.parse(view.categories);
      console.log('Parsed category IDs:', categoryIds);
      setVisibleCategoryIds(categoryIds);
    } catch (e) {
      console.error('Failed to parse view:', e);
    }
  }
};
const handleRefreshNotes = async () => {
  try {
    console.log('🔄 Refreshing notes list...');
    await refreshNotes(); // This calls loadNotes() from NotesContext
    console.log('✅ Notes refreshed successfully');
  } catch (error) {
    console.error('❌ Failed to refresh notes:', error);
  }
};



  const handleModalToggleCategory = (categoryId) => {
    setModalVisibleCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleModalToggleAll = (showAll) => {
    if (showAll) {
      setModalVisibleCategories(categories.map((cat) => cat.id));
    } else {
      setModalVisibleCategories([]);
    }
  };

  const loadSavedViews = async () => {
    const user = getCurrentUser();
    try {
      const response = await fetch(`/api/users/${user.user_id}/category-views`, {
        credentials: 'include',
      });
      const data = await response.json();
      setSavedViews(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load saved views:', e);
    }
  };

  useEffect(() => {
    loadSavedViews();
  }, []);

  useEffect(() => {
    // On initial load, set all categories visible
    if (categories.length > 0 && selectedViewId === 'all') {
      setVisibleCategoryIds(categories.map((cat) => cat.id));
    }
  }, [categories, selectedViewId]);

  if (loading) {
    return (
      <div className={`${styles.leftPanel} ${isLeftPanelVisible ? styles.open : styles.closed}`}>
        Loading categories...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.leftPanel} ${isLeftPanelVisible ? styles.open : styles.closed}`}>
        Error: {error}
      </div>
    );
  }

  const handleToggleCategory = (categoryId) => {
    setVisibleCategoryIds((prev) => {
      if (!prev) return [categoryId];
      return prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId];
    });
  };

  const handleToggleAll = (showAll) => {
    if (showAll) {
      setVisibleCategoryIds(categories.map((cat) => cat.id));
    } else {
      setVisibleCategoryIds([]);
    }
  };

  return (
    <>
      <div
        className={`
          ${styles.leftPanel} 
          ${isLeftPanelVisible ? styles.open : styles.closed}
          ${isCollapsed ? styles.collapsed : ''}
        `}
        role="complementary"
        aria-hidden={!isLeftPanelVisible}
      >
        <div className={styles.panelHeader}>
          <div className={styles.logoContainer}>
            {!isCollapsed && (
              <>
                <img
                  src="/images/notesdesk.png"
                  alt="NotesDesk Logo"
                  className={styles.logoImage}
                />
              </>
            )}
            <button
              onClick={toggleLeftPanel}
              className={styles.collapseButton}
              title="Hide panel"
            >
              <ChevronLeft size={18} />
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <>
            <div className={styles.leftPanelControls}>
              <select
                className={styles.viewDropdown}
                defaultValue="all"
                onChange={(e) => {
                  console.log('Dropdown change triggered with:', e.target.value);
                  handleViewChange(e.target.value);
                }}
              >

                <option value="all">All Notes</option>
                {savedViews.map((view) => (
                  <option key={view.id} value={view.id.toString()}>
                    {view.name}
                  </option>
                ))}
              </select>
                    <button
                      onClick={() => setIsAddCategoryModalOpen(true)}
                      className={styles.controlButton}
                      title="Add Category"
                    >
                      <Plus size={16} />
                    </button>

              <button
                onClick={() => setIsFilterModalOpen(true)}
                className={styles.controlButton}
                title="Manage Views"
              >
                <Settings size={16} />
              </button>

              <button
                onClick={handleSearchClick}
                className={styles.controlButton}
                title="Search Notes"
              >
                <Search size={16} />
              </button>
            </div>
            {/* NEW SORT SECTION BELOW */}
                <div className={styles.sortSeparator}></div>

                <div className={styles.sortControls}>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                    className={`${styles.controlButton} ${sortOrder.includes('est') ? styles.active : ''}`}
                    title={sortOrder === 'newest' ? 'Sort by Oldest First' : 'Sort by Newest First'}
                  >
                    📅
                  </button>

                  <button
                    onClick={() => setSortOrder('title')}
                    className={`${styles.controlButton} ${sortOrder === 'title' ? styles.active : ''}`}
                    title="Sort by Title"
                  >
                    🔤
                  </button>
                   {/* SINGLE FOLDER TOGGLE */}
                          <button
                            onClick={() => {
                              const allCategoryIds = categories.map(cat => cat.id);
                              const areAllExpanded = allCategoryIds.every(id => expandedCategories.has(id));
                              
                              if (areAllExpanded) {
                                // Collapse all
                                setExpandedCategories(new Set());
                                localStorage.setItem('expandedCategories', JSON.stringify([]));
                              } else {
                                // Expand all
                                setExpandedCategories(new Set(allCategoryIds));
                                localStorage.setItem('expandedCategories', JSON.stringify(allCategoryIds));
                              }
                            }}
                            className={styles.controlButton}
                            title={
                              categories.length > 0 && categories.every(cat => expandedCategories.has(cat.id))
                                ? "Collapse All Categories" 
                                : "Expand All Categories"
                            }
                          >
                            {categories.length > 0 && categories.every(cat => expandedCategories.has(cat.id)) ? '📁' : '📂'}
                          </button>
                   
                   <button
                        onClick={() => {
                          const nextSort = folderSort === 'none' ? 'top' : folderSort === 'top' ? 'bottom' : 'none';
                          setFolderSort(nextSort);
                        }}
                        className={`${styles.controlButton} ${folderSort !== 'none' ? styles.active : ''}`}
                        title={`Folder Sort: ${folderSort === 'top' ? 'Top' : folderSort === 'bottom' ? 'Bottom' : 'Mixed'}`}
                      >
                        🗂️
                      </button>

                     <button
                        onClick={handleRefreshNotes}
                        className={styles.controlButton}
                        title="Refresh Notes List"
                      >
                        🔄
                      </button>
                                          
                </div>

            <div className={styles.panelDock}>
              <NotesTreeList
                onNoteSelect={onNoteSelect}
                selectedNoteId={selectedNoteId}
                visibleCategoryIds={visibleCategoryIds}
                expandedCategories={expandedCategories}
                setExpandedCategories={setExpandedCategories}
                sortOrder={sortOrder}  // Add this line
                folderSort={folderSort}
              />
            </div>

            <div className={styles.logoutContainer}>
              <button onClick={handleLogout} className={styles.logoutButton}>
                Logout
              </button>
            </div>
          </>
        )}

        {isCollapsed && (
          <div className={styles.collapsedContent}>
            <button
              className={styles.collapsedButton}
              onClick={() => setIsCollapsed(false)}
              title="Expand Panel"
            >
              <Menu size={18} />
            </button>
          </div>
        )}
      </div>

      {isSearchModalOpen && (
        <SearchModal
          isOpen={isSearchModalOpen}
          onClose={handleCloseSearchModal}
          onNoteSelect={onNoteSelect}
        />
      )}

     {isFilterModalOpen && (
 <CategoryFilterModal
   isOpen={true}
   onClose={() => setIsFilterModalOpen(false)}
   categories={categories}
   visibleCategories={modalVisibleCategories}
   onToggleCategory={handleModalToggleCategory}
   onToggleAll={handleModalToggleAll}
   onViewChange={loadSavedViews}
 />
)}

{isAddCategoryModalOpen && (
 <AddCategoryModal
   isOpen={isAddCategoryModalOpen}
   onClose={() => setIsAddCategoryModalOpen(false)}
   onCreate={addCategory}
 />
)}
</>
);
};

export default React.memo(LeftPanel);
